/**
 * @fileoverview Helpers varios.
 */

/**
 * Auto-formatea un string a XXX-NNNN: mete guion automáticamente
 * después del 3er carácter alfa, fuerza mayúsculas, recorta a 8.
 * @param {string} raw
 * @returns {string}
 */
export function formatearCodigo(raw) {
    const cleaned = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (cleaned.length <= 3) return cleaned;
    const prefix = cleaned.slice(0, 3).replace(/[^A-Z]/g, "");
    const suffix = cleaned.slice(3, 7).replace(/[^0-9]/g, "");
    return prefix.length === 3 ? `${prefix}-${suffix}` : prefix;
}

/**
 * Valida si un string cumple `XXX-NNNN`.
 * @param {string} codigo
 */
export function esCodigoValido(codigo) {
    return /^[A-Z]{3}-\d{4}$/.test((codigo || "").toUpperCase());
}

/**
 * Extrae `codigo` de un string que puede ser una URL completa o el código
 * directo. Útil para parsear el contenido de un QR.
 * @param {string} raw
 * @returns {string | null}
 */
export function extraerCodigoDeQR(raw) {
    if (!raw) return null;
    try {
        // Intenta parsear como URL completa con ?codigo=
        const url = new URL(raw);
        const c = url.searchParams.get("codigo");
        if (c && esCodigoValido(c.toUpperCase())) return c.toUpperCase();
    } catch {
        // intencional: si `new URL` lanza es porque el QR contiene el
        // código directo en lugar de una URL; caemos al formateo manual.
    }
    const formatted = formatearCodigo(raw);
    return esCodigoValido(formatted) ? formatted : null;
}

/**
 * @param {(...args: any[]) => void} fn
 * @param {number} ms
 */
export function debounce(fn, ms) {
    /** @type {ReturnType<typeof setTimeout> | null} */
    let timer = null;
    return (/** @type {any[]} */ ...args) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => fn(...args), ms);
    };
}

/**
 * Convierte una excepción del cliente HTTP en un mensaje legible.
 * Prefiere el `mensaje` contextual del backend (cuando viene incluido en
 * `detail`, p.ej. token_expirado con la fecha exacta) y cae al mapeo
 * estático por `code` solo si no hay mensaje.
 * @param {unknown} err
 * @returns {string}
 */
export function mensajeDeError(err) {
    if (err && typeof err === "object") {
        const e = /** @type {any} */ (err);
        if (typeof e.message === "string" && e.code && e.message !== e.code) {
            // ApiError construye `message` a partir del mensaje pasado;
            // si difiere del code es porque traemos el mensaje contextual del backend.
            return e.message;
        }
        if (typeof e.code === "string") return mensajeError(e.code);
    }
    return mensajeError(null);
}

/**
 * Convierte el código de error del backend a mensaje legible.
 * @param {string | undefined | null} code
 * @returns {string}
 */
export function mensajeError(code) {
    /** @type {Record<string,string>} */
    const map = {
        token_no_existe: "No encontramos ese código. Verifica que esté bien escrito.",
        token_completado:
            "Este código ya fue usado completamente. Si crees que es un error, acércate al personal del foro.",
        token_cerrado_por_foro: "El foro ya cerró. Este código no puede usarse para una sesión nueva.",
        token_expirado: "Este código ha expirado. Solicita uno nuevo al personal del foro.",
        token_no_reanudable:
            "Este código no se puede reanudar. Si nunca lo iniciaste, escríbelo en la pantalla principal.",
        forum_cerrado: "El foro ya cerró. No es posible iniciar una nueva participación.",
        statement_no_existe: "Esa afirmación ya no está disponible.",
        rate_limited: "Demasiadas solicitudes en poco tiempo. Intenta de nuevo en un minuto.",
        network_error: "Sin conexión. Revisa tu internet e intenta otra vez.",
        invalid_response: "La respuesta del servidor no se pudo procesar. Vuelve a intentar.",
        invalid_token: "Tu sesión expiró. Vuelve a ingresar tu código.",
        missing_authorization: "Tu sesión expiró. Vuelve a ingresar tu código.",
        participacion_completada: "Esta participación ya terminó.",
        // Fallbacks por status HTTP cuando el detalle no se pudo parsear:
        http_404: "No encontramos lo que pediste. Verifica tu código.",
        http_409: "Hubo un conflicto: el foro pudo haber cerrado o tu código ya se usó.",
        http_422: "Algunos datos no son válidos. Revísalos e intenta de nuevo.",
        http_429: "Demasiadas solicitudes en poco tiempo. Intenta de nuevo en un minuto.",
        http_500: "El servidor tuvo un problema. Intenta de nuevo en un momento.",
        http_503: "El servicio no está disponible ahora mismo. Intenta más tarde.",
    };
    return code && map[code] ? map[code] : "Algo no salió bien. Intenta de nuevo.";
}
